import React, { useEffect, useMemo, useRef } from 'react'
import ReactDOM from 'react-dom'
import PropTypes from 'prop-types'

import './Tooltip.css'

const CLASS = 'insights-tooltip'

const TOLERANCE = 28

let tooltip = null
let hideEvents = []
let currentTarget = null
let exclusiveMode = false

const hideTooltip = event => {
  for (const hideEvent of hideEvents) {
    const [param, cmd] = hideEvent.split('&')
    const [type, element] = param.split(':')
    if (currentTarget && (!element || 'target' === element)) currentTarget.removeEventListener(type, hideTooltip)
    else if ('body' === element) document.removeEventListener(type, hideTooltip)
    if (event && event.stopPropagation && event.type === type && 'propagate' !== cmd && 'body' !== element)
      event.stopPropagation()
  }
  if (tooltip) {
    document.body.removeChild(tooltip)
    tooltip = null
  }
  hideEvents = []
  currentTarget = null
  exclusiveMode = false
}

const getPositionRect = ({ position, target }) => {
  const result = {}
  const targetRect = target.getBoundingClientRect()
  const bodyRect = document.body.getBoundingClientRect()
  if ('right-down' === position) {
    result.top = targetRect.y - bodyRect.y + Math.round(targetRect.height / 2)
    result.left = targetRect.x - bodyRect.x + targetRect.width
  } else if ('up-right' === position) {
    result.bottom = bodyRect.height - targetRect.y + bodyRect.y
    result.left = targetRect.x - bodyRect.x + Math.round(targetRect.width / 2)
  } else if ('up-left' === position) {
    result.bottom = bodyRect.height - targetRect.y + bodyRect.y
    result.right = bodyRect.width - targetRect.x + bodyRect.x - Math.round(targetRect.width / 2)
  }
  return result
}

const getPosition = ({ position, target, width }) => {
  const positionRect = getPositionRect({ position, target })
  const bodyRect = document.body.getBoundingClientRect()
  if ('up-right' === position && positionRect.left + width + TOLERANCE >= bodyRect.right) return 'up-left'
  return position
}

const tryTooltip = ({ className, content }, callback) => {
  tooltip = document.createElement('div')
  tooltip.style.visibility = 'hidden'
  tooltip.classList.add(CLASS)
  if (className) tooltip.classList.add(className)
  const tooltipContent = React.isValidElement(content) ? content : <div className={`${CLASS}-content`}>{content}</div>
  ReactDOM.render(tooltipContent, document.body.appendChild(tooltip), function () {
    if (tooltip) {
      this.classList.add(`${CLASS}-content`)
      const tooltipRect = tooltip.getBoundingClientRect()
      document.body.removeChild(tooltip)
      callback({ width: tooltipRect.width, height: tooltipRect.height })
    }
  })
}

const createTooltip = ({ className, content, position, target, hideOn, persistent }) => {
  const positionRect = getPositionRect({ position, target })
  tooltip = document.createElement('div')
  tooltip.classList.add(CLASS)
  tooltip.classList.add(`${CLASS}-${position}`)
  if (className) tooltip.classList.add(className)
  for (const key of ['top', 'left', 'right', 'bottom']) {
    if (key in positionRect) tooltip.style[key] = `${positionRect[key]}px`
  }
  const tooltipContent = React.isValidElement(content) ? (
    content
  ) : (
    <div className={`${CLASS}-content ${CLASS}-content-${position}`}>{content}</div>
  )
  ReactDOM.render(tooltipContent, document.body.appendChild(tooltip), function () {
    this.classList.add(`${CLASS}-content`)
    this.classList.add(`${CLASS}-content-${position}`)
  })
  exclusiveMode = persistent
  currentTarget = target
  hideEvents = hideOn.slice()
  for (const hideEvent of hideEvents) {
    const [param] = hideEvent.split('&')
    const [type, element] = param.split(':')
    if (!element || 'target' === element) target.addEventListener(type, hideTooltip)
    else if ('body' === element) document.addEventListener(type, hideTooltip)
  }
}

const showTooltip = ({ className, content, position, target, hideOn, persistent }) => {
  tryTooltip({ className, content }, ({ width }) => {
    const newPosition = getPosition({ position, target, width })
    createTooltip({ className, content, position: newPosition, target, hideOn, persistent })
  })
}

const Tooltip = props => {
  const className = props.className || null
  const position = props.position || 'right-down'
  const persistent = props.persistent | false
  const showOn = useMemo(
    () => (props.showOn ? (Array.isArray(props.showOn) ? props.showOn : props.showOn.split(' ')) : []),
    [props.showOn]
  )
  const hideOn = useMemo(
    () => (props.hideOn ? (Array.isArray(props.hideOn) ? props.hideOn : props.hideOn.split(' ')) : []),
    [props.hideOn]
  )

  const contentRef = useRef()
  contentRef.current = props.content || props.children || ''

  const onTargetRef = useRef()
  onTargetRef.current = props.onTarget || null

  useEffect(() => {
    const target = onTargetRef.current ? onTargetRef.current() : null
    if (target) {
      const onShow = event => {
        if (exclusiveMode && !persistent) return
        if (event && 'click' === event.type && event.stopPropagation) event.stopPropagation()
        if (tooltip) hideTooltip()
        if (contentRef.current)
          showTooltip({ className, content: contentRef.current, position, target, hideOn, persistent })
      }
      for (const event of showOn) target.addEventListener(event, onShow)
      return () => {
        for (const event of showOn) target.removeEventListener(event, onShow)
        hideTooltip()
      }
    } else return () => hideTooltip()
  }, [className, hideOn, persistent, position, showOn])

  return null
}

Tooltip.propTypes = {
  className: PropTypes.string,
  children: PropTypes.any,
  content: PropTypes.any,
  showOn: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  hideOn: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  position: PropTypes.string,
  persistent: PropTypes.any,
  propagation: PropTypes.any,
  onTarget: PropTypes.func
}

Tooltip.defaultProps = {
  position: 'right-down',
  showOn: 'click',
  hideOn: 'click click:body wheel:body scroll:body',
  persistent: false
}

export default Tooltip
